import {type AppError, NetworkError} from '@packages/utils';
import {useQueryClient} from '@tanstack/react-query';
import {useEffect, useRef, useState} from 'react';
import {
  GTAG_CMD_EVENT,
  GTAG_EVENT_NAMES,
  type GtagLocation,
} from '../../../config/constants/gtag';
import {DUPLICATED_EMAIL_MESSAGE} from '../../../core/entities/mypage/emailConstants';
import {MAIL_ADDRESS_VALIDATION_MSG} from '../../../core/entities/mypage/schema';
import {
  useGeneralProfiles,
  usePostAcEmailAuth,
  usePutGeneralProfiles,
} from '../../../core/repositories/mypageRepository';
import {useCheckAcEmail} from '../../../core/repositories/registerRepository';
import {
  buildAcEmailCheckError,
  shouldCheckAcEmail,
} from '../../../core/usecases/acEmail/acEmailPreCheck';
import {
  buildDuplicatedEmailErrors,
  isDuplicatedEmailError,
} from '../../../core/usecases/acEmail/duplicatedEmailErrors';
import {useAuth} from '../../../core/usecases/auth/AuthContext';
import {emailRegex} from '../../services/emailValidation';
import {showErrorSnackBar, showSuccessSnackBar} from '../SnackBar';
import {shouldShowAcEmailInputForm} from './shouldShowAcEmailInputForm';

type CastedError = {message: AppError};

type UseAcEmailVerificationArgs = {
  gtagLocation: GtagLocation;
  onSubmitted?: () => void;
};

export const useAcEmailVerification = ({
  gtagLocation,
  onSubmitted,
}: UseAcEmailVerificationArgs) => {
  const queryClient = useQueryClient();
  const {user} = useAuth();
  const {data, isLoading} = useGeneralProfiles({});
  const [inputValue, setInputValue] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const generalProfile = data?.ok ? data.value : undefined;
  const acEmail = generalProfile?.acEmail ?? '';
  const pendingAcEmail = generalProfile?.pendingAcEmail ?? '';
  const hasNoAcEmail = acEmail === '' && pendingAcEmail === '';
  // 大学メールを持たずに大学認証済みの場合は学生証で在籍確認が取れている
  const isVerifiedByStudentCard =
    !!generalProfile && hasNoAcEmail && user?.isStudentVerified === true;

  const isInputFormShown = shouldShowAcEmailInputForm({
    isLoading,
    isProfileLoaded: !!generalProfile,
    acEmail,
    pendingAcEmail,
    isVerifiedByStudentCard,
  });
  const sentImpression = useRef(false);
  useEffect(() => {
    if (!isInputFormShown || sentImpression.current) {
      return;
    }
    if (window?.gtag) {
      window.gtag(
        GTAG_CMD_EVENT,
        GTAG_EVENT_NAMES.IMP_AC_EMAIL_VERIFICATION_FORM,
        {
          location: gtagLocation,
        },
      );
      sentImpression.current = true;
    }
  }, [isInputFormShown, gtagLocation]);

  const checkAcEmail = useCheckAcEmail();

  const saveGeneralProfiles = usePutGeneralProfiles({
    onSuccessFnc() {
      setIsSubmitting(false);
      showSuccessSnackBar({title: '送信しました', message: 'done'});
      void queryClient.invalidateQueries(['generalProfile']);
      onSubmitted?.();
    },
    onErrorFnc(error: unknown) {
      setIsSubmitting(false);

      if (isDuplicatedEmailError(error)) {
        showErrorSnackBar({
          isNetworkError: false,
          title: 'エラー',
          message: DUPLICATED_EMAIL_MESSAGE,
        });
        setErrorMessage(
          buildDuplicatedEmailErrors(error.responseData?.message).acEmail,
        );
        return;
      }

      showErrorSnackBar({
        isNetworkError: (error as CastedError).message === NetworkError,
      });
    },
  });

  const postAcEmailAuth = usePostAcEmailAuth({
    onSuccessFnc() {
      showSuccessSnackBar({title: '送信しました', message: 'done'});
    },
    onErrorFnc(error: unknown) {
      showErrorSnackBar({
        isNetworkError: (error as CastedError).message === NetworkError,
      });
    },
  });

  // 事前チェックの結果を errorMessage に反映し、保存に進んでよいかを返す
  const passesPreCheck = async () => {
    if (!shouldCheckAcEmail(inputValue, acEmail || pendingAcEmail)) {
      return true;
    }

    const checkResult = await checkAcEmail
      .mutateAsync({requestBody: {email: inputValue}})
      .catch(() => undefined);

    // チェックできなかった場合は保存に進まず、時間を置いて再実行してもらう
    if (!checkResult?.ok) {
      showErrorSnackBar({isNetworkError: true});
      return false;
    }

    const checkError = buildAcEmailCheckError(checkResult.value);
    if (checkError) {
      setErrorMessage(checkError);
      return false;
    }

    return true;
  };

  const submit = async () => {
    // プロフィール未取得時はフォーム自体を描画しないため、ここには到達しない
    if (!generalProfile) {
      return;
    }

    setErrorMessage(undefined);

    if (!emailRegex.test(inputValue)) {
      setErrorMessage(MAIL_ADDRESS_VALIDATION_MSG);
      return;
    }

    setIsSubmitting(true);

    if (!(await passesPreCheck())) {
      setIsSubmitting(false);
      return;
    }

    // 保存APIは電話番号とログイン用メールも必須のため、取得済みの現在値をそのまま送る
    saveGeneralProfiles.mutate({
      privateEmail: generalProfile.privateEmail,
      telNumber: generalProfile.telNumber,
      acEmail: inputValue,
    });
  };

  return {
    // 保存APIが現在値を必要とするため、取得できるまでフォームを描画しない
    isProfileLoaded: !!generalProfile,
    isLoading,
    acEmail,
    pendingAcEmail,
    isVerifiedByStudentCard,
    inputValue,
    errorMessage,
    isSubmitting,
    onChangeInput: setInputValue,
    onSubmit: () => {
      if (window?.gtag) {
        window.gtag(
          GTAG_CMD_EVENT,
          GTAG_EVENT_NAMES.CLICK_AC_EMAIL_VERIFICATION_SUBMIT,
          {location: gtagLocation},
        );
      }
      void submit();
    },
    onResend: () => {
      if (window?.gtag) {
        window.gtag(
          GTAG_CMD_EVENT,
          GTAG_EVENT_NAMES.CLICK_AC_EMAIL_VERIFICATION_RESEND,
          {location: gtagLocation},
        );
      }
      postAcEmailAuth.mutate();
    },
    isResending: postAcEmailAuth.isPending,
  };
};
